import { __assign, __awaiter, __generator } from "tslib";
import { CacheLookupPolicy, InteractionRequiredAuthError, } from '@azure/msal-browser';
import { AuthType, } from '@office-start/auth-types';
import { fetchOfficeComponentContext } from '@office-start/component-context-service';
import { showErrorDialog } from '@office-start/error-dialog-service';
import { fetchFeatureGates, getFeatureGates, } from '@office-start/feature-gates-service';
import { fetchFeatureSettings, getFeatureSettings, } from '@office-start/feature-settings-service';
import { now } from '@office-start/performance-utility';
import { logDiagnostic, logError, logRequest, } from '@office-start/telemetry-service';
import { getErrorString } from './getErrorString';
import { getMsalClient, tokenResourceIdMap } from './getMsalClient';
import { GetTokenType, } from './getTokenParams';
import { initializePairwiseBroker } from './initializePairwiseBroker';
import { redirectToLogin } from './redirectToLogin';
var pendingSsoSilentPromise;
export function getToken(params) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, getTokenInternal(__assign({ type: GetTokenType.DEFAULT }, params))];
        });
    });
}
export function getNestedAppToken(params) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, getTokenInternal(params)];
        });
    });
}
function getTokenInternal(params) {
    return __awaiter(this, void 0, void 0, function () {
        var context, identity, featureGatesResult, featureGates, _a, featureSettingsResult, featureSettings, _b, tokenConsumer, resourceId, scope, forceFetch, silent, err_1;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0: return [4 /*yield*/, fetchOfficeComponentContext()];
                case 1:
                    context = _c.sent();
                    return [4 /*yield*/, context.getIdentity()];
                case 2:
                    identity = _c.sent();
                    featureGatesResult = getFeatureGates();
                    if (!(featureGatesResult === null || featureGatesResult === void 0 ? void 0 : featureGatesResult.result)) return [3 /*break*/, 3];
                    _a = featureGatesResult;
                    return [3 /*break*/, 5];
                case 3: return [4 /*yield*/, fetchFeatureGates()];
                case 4:
                    _a = _c.sent();
                    _c.label = 5;
                case 5:
                    featureGates = _a;
                    featureSettingsResult = getFeatureSettings();
                    if (!(featureSettingsResult === null || featureSettingsResult === void 0 ? void 0 : featureSettingsResult.result)) return [3 /*break*/, 6];
                    _b = featureSettingsResult;
                    return [3 /*break*/, 8];
                case 6: return [4 /*yield*/, fetchFeatureSettings()];
                case 7:
                    _b = _c.sent();
                    _c.label = 8;
                case 8:
                    featureSettings = _b;
                    tokenConsumer = params.tokenConsumer, resourceId = params.resourceId, scope = params.scope, forceFetch = params.forceFetch, silent = params.silent;
                    if (!(identity.authType === AuthType.MSA)) return [3 /*break*/, 12];
                    _c.label = 9;
                case 9:
                    _c.trys.push([9, 11, , 12]);
                    return [4 /*yield*/, initializePairwiseBroker()];
                case 10:
                    _c.sent();
                    return [3 /*break*/, 12];
                case 11:
                    err_1 = _c.sent();
                    logError({
                        eventName: 'Msal_PairwiseBroker_Init_Failure',
                        errorType: 'Authentication_Failed',
                        debugInfo: "failed with exception: ".concat(err_1),
                    });
                    return [3 /*break*/, 12];
                case 12: return [2 /*return*/, new Promise(function (resolve, _reject) {
                        if (context.inMockMode) {
                            resolve({
                                success: true,
                                format: 'JWT',
                                source: 'OfficeHome',
                                token: 'mocktoken',
                            });
                            return;
                        }
                        getMsalClient()
                            .then(function (msalClient) {
                            var start = now();
                            var tokenScope = getTokenScope(tokenConsumer, resourceId, scope);
                            if (params.type === GetTokenType.NESTED_APP) {
                                tokenScope = scope !== null && scope !== void 0 ? scope : '';
                            }
                            if (!tokenScope) {
                                var err = 'No token resourceID provided';
                                logTokenFetch({
                                    params: params,
                                    latency: -1,
                                    success: false,
                                    fromCache: false,
                                    error: err,
                                });
                                resolve(getFailureResponse(err));
                                return;
                            }
                            var scenarioId = params.type === GetTokenType.NESTED_APP
                                ? "nestedApp" /* AuthScenarios.nestedApp */
                                : "general" /* AuthScenarios.general */;
                            var authRequest = {
                                scopes: [tokenScope],
                                scenarioId: scenarioId,
                                forceRefresh: forceFetch,
                                cacheLookupPolicy: CacheLookupPolicy.Default,
                            };
                            if (params.type == GetTokenType.NESTED_APP) {
                                authRequest.redirectUri = params.nestedClientRedirectUri;
                                authRequest.correlationId = params.requestId;
                                authRequest.cacheLookupPolicy =
                                    CacheLookupPolicy.RefreshTokenAndNetwork; // This is to ensure we do not store the nested app tokens in host cache.
                                authRequest.extraQueryParameters = {
                                    client_id: params.nestedClientId,
                                    brk_client_id: params.clientId,
                                    brk_redirect_uri: params.redirectUri,
                                };
                                authRequest.tokenBodyParameters = {
                                    client_id: params.nestedClientId,
                                    brk_client_id: params.clientId,
                                    brk_redirect_uri: params.redirectUri,
                                };
                                authRequest.storeInCache = {
                                    idToken: false,
                                    accessToken: false,
                                };
                            }
                            msalClient
                                .acquireTokenSilent(authRequest)
                                .then(function (tokenResponse) {
                                var end = now();
                                logTokenFetch({
                                    params: params,
                                    latency: end -
                                        (params.ssoSilentInfo
                                            ? params.ssoSilentInfo.originalStartTime
                                            : start),
                                    success: true,
                                    fromCache: tokenResponse.fromCache,
                                });
                                resolve(getSuccessResponse(params.type, tokenResponse));
                            })
                                .catch(function (error) {
                                var _a, _b, _c, _d, _e, _f, _g;
                                var msalError = error;
                                var end = now();
                                if (((_b = (_a = featureGates.result) === null || _a === void 0 ? void 0 : _a.authGates) === null || _b === void 0 ? void 0 : _b.authSilentSsoEnabled) &&
                                    !params.ssoSilentInfo &&
                                    msalError.errorCode === 'invalid_grant' &&
                                    msalError.subError === 'bad_token') {
                                    if (pendingSsoSilentPromise) {
                                        pendingSsoSilentPromise.then(function (status) {
                                            pendingSsoSilentPromise = undefined;
                                            resolve(getTokenInternal(__assign(__assign({}, params), { ssoSilentInfo: {
                                                    ssoSilentStatus: status
                                                        ? 0 /* SsoSilentStatus.ssoSilentSuccess */
                                                        : 1 /* SsoSilentStatus.ssoSilentFailure */,
                                                    originalStartTime: start,
                                                } })));
                                        });
                                        return;
                                    }
                                    var ssoStartTime_1 = now();
                                    pendingSsoSilentPromise = msalClient
                                        .ssoSilent({
                                        loginHint: identity.userPrincipalName,
                                        scopes: [
                                            (_e = (_d = (_c = featureSettings.result) === null || _c === void 0 ? void 0 : _c.authOptions) === null || _d === void 0 ? void 0 : _d.officeHomeTokenScope) !== null && _e !== void 0 ? _e : '',
                                        ],
                                        scenarioId: "ssoSilent" /* AuthScenarios.ssoSilent */,
                                    })
                                        .then(function (_r) {
                                        var ssoEndTime = now();
                                        logRequest({
                                            eventName: 'Msal_Sso_Silent',
                                            httpCode: 200,
                                            fromCache: false,
                                            latency: Math.round(ssoEndTime - ssoStartTime_1),
                                        });
                                        pendingSsoSilentPromise = undefined;
                                        resolve(getTokenInternal(__assign(__assign({}, params), { ssoSilentInfo: {
                                                ssoSilentStatus: 0 /* SsoSilentStatus.ssoSilentSuccess */,
                                                originalStartTime: start,
                                            } })));
                                        return true;
                                    })
                                        .catch(function (e) {
                                        var ssoEndTime = now();
                                        logRequest({
                                            eventName: 'Msal_Sso_Silent',
                                            httpCode: 500,
                                            fromCache: false,
                                            latency: Math.round(ssoEndTime - ssoStartTime_1),
                                            error: getErrorString(e),
                                        });
                                        logTokenFetch({
                                            params: __assign(__assign({}, params), { ssoSilentInfo: {
                                                    ssoSilentStatus: 1 /* SsoSilentStatus.ssoSilentFailure */,
                                                    originalStartTime: start,
                                                } }),
                                            latency: ssoEndTime - start,
                                            success: false,
                                            fromCache: false,
                                            error: msalError,
                                        });
                                        pendingSsoSilentPromise = undefined;
                                        // Since SSO silent failed, we need to redirect to login to get new auth code.
                                        redirectToLogin(identity, 'SsoSilentFailed', {
                                            IdentityIssuedTime: identity.identityIssuedTimeUnixMs ||
                                                -1,
                                        });
                                        return false;
                                    });
                                    return;
                                }
                                // The silent sso flow logs it's own event so this event is logged after that logic runs to avoid double logging a token fetch event.
                                logTokenFetch({
                                    params: params,
                                    latency: end -
                                        (params.ssoSilentInfo
                                            ? params.ssoSilentInfo.originalStartTime
                                            : start),
                                    success: false,
                                    fromCache: false,
                                    error: msalError,
                                });
                                // For some reason msal account is no longer in local storage. Need to reauth.
                                if (msalError.errorCode === 'no_account_error') {
                                    logDiagnostic({
                                        eventName: 'HybridAuth_NoAccount_StorageLength',
                                        params: { length: window.localStorage.length },
                                    });
                                    redirectToLogin(identity, 'NoAccountOnToken');
                                    return;
                                }
                                // Check if either the OfficeHome or Substrate token fetches fail with an
                                // InteractionRequired error to see if our refresh token is now invalid.
                                // If so, redirect to login to get a new refresh token.
                                // The refresh token can become invalid for many reasons such as: tenant admin has
                                // revoked all the user's sessions, reset their password, or a conditional access policy
                                // limits the lifetime of user sessions.
                                // Note: this logic isn't quite correct, but was done because the Substrate token
                                // is not part of any app-specific Conditonal Access policy (like Exchange or SharePoint), and
                                // we fetch a Substrate token on almost all routes on page load (mainly to call edgeworth).
                                // Therefore if Substrate token fetch fails with InteractionRequired, then that means the refresh token
                                // is not valid. Ideally the logic should take into account the initial route when loading the page and
                                // check if any required tokens on that route are failing.
                                // The OfficeHome token is also included in this check since that also can be used to indicate a bad refresh
                                // token.
                                if (((_g = (_f = featureGates.result) === null || _f === void 0 ? void 0 : _f.authGates) === null || _g === void 0 ? void 0 : _g.authSubstrateTokenCheck) &&
                                    msalError instanceof InteractionRequiredAuthError &&
                                    silent !== false &&
                                    (tokenConsumer === 'OfficeHome' ||
                                        tokenConsumer === 'Substrate')) {
                                    redirectToLogin(identity, 'RefreshTokenInvalid', {
                                        IdentityIssuedTime: identity.identityIssuedTimeUnixMs || -1,
                                    });
                                    return;
                                }
                                if (msalError instanceof InteractionRequiredAuthError &&
                                    silent === false &&
                                    params.capDialogData) {
                                    showErrorDialog({
                                        category: params.capDialogData.category,
                                        error: getConditionalAccessErrorType(msalError.subError),
                                        fileType: params.capDialogData.fileType,
                                        onActionClicked: function () {
                                            authRequest.scenarioId =
                                                "popup" /* AuthScenarios.popup */;
                                            msalClient
                                                .acquireTokenPopup(authRequest)
                                                .then(function (r) {
                                                logTokenFetch({
                                                    params: params,
                                                    latency: -1,
                                                    success: true,
                                                    fromCache: false,
                                                    error: 'acquired from popup',
                                                });
                                                resolve(getSuccessResponse(params.type, r));
                                            })
                                                .catch(function (e) {
                                                logTokenFetch({
                                                    params: params,
                                                    latency: -1,
                                                    success: false,
                                                    fromCache: false,
                                                    error: e,
                                                });
                                                resolve(getFailureResponse(e));
                                            });
                                        },
                                        onClose: function () {
                                            resolve(getFailureResponse('CAP dialog closed by user'));
                                        },
                                    });
                                }
                                else {
                                    resolve(getFailureResponse(msalError));
                                }
                            });
                        })
                            .catch(function (_) {
                            resolve(getFailureResponse('Error getting msal client'));
                        });
                    })];
            }
        });
    });
}
var substrateScopeMap = new Map([
    ['Sydney', 'sydney/.default'],
    ['SubstrateSearch', 'search/.default'],
]);
function getTokenScope(tokenConsumer, resourceId, scope) {
    // To match the behavior of our Angular token fetching, trim trailing slashes for MetaOS resourceIds.
    // TODO: this should not be required. Look into safely removing this trimming logic if we determine no MetaOS
    // apps have resource Ids with trailing slashes.
    if (tokenConsumer === 'MetaOS' &&
        resourceId &&
        resourceId.charAt(resourceId.length - 1) === '/') {
        resourceId = resourceId.substring(0, resourceId.length - 1);
    }
    // TODO: Add DesignerAppService resource id and scope to FeatureSettings.resources
    if (tokenConsumer === 'DesignerAppService') {
        return 'https://designerappservice.officeapps.live.com/designerappservice.all';
    }
    // TODO: Add FronrDoor resource id and scope to FeatureSettings.resources
    if (tokenConsumer === 'AiHubFrontDoor') {
        return 'd985dc98-494e-4460-aa2b-8497af04e141/.default';
    }
    // TODO: Add PowerPlatform resource id and scope to FeatureSettings.resources
    if (tokenConsumer === 'PowerPlatform') {
        return 'https://api.powerplatform.com/.default';
    }
    // TODO: Add PowerPlatformPreProd resource id and scope to FeatureSettings.resources
    if (tokenConsumer === 'PowerPlatformPreProd') {
        return 'https://api.preprod.powerplatform.com/.default';
    }
    // TODO: Add Sydney resource id and scope to .default
    var substrateScope = substrateScopeMap.get(tokenConsumer);
    if (!!substrateScope) {
        var substrate = tokenResourceIdMap.get('Substrate');
        if (substrate && substrate.id) {
            return "".concat(substrate.id, "/").concat(substrateScope);
        }
        return '';
    }
    if (resourceId) {
        if (scope) {
            return resourceId + '/' + scope;
        }
        return resourceId + '/.default';
    }
    var tokenResource = tokenResourceIdMap.get(tokenConsumer);
    if (tokenResource && tokenResource.id) {
        if (tokenResource.includesScope) {
            return tokenResource.id;
        }
        if (scope) {
            return tokenResource.id + '/' + scope;
        }
        return tokenResource.id + '/.default';
    }
    return '';
}
function logTokenFetch(param) {
    var params = param.params;
    if (!param.fromCache) {
        var eventNamePrefix = 'Msal_Token_Fetch_';
        if (params.ssoSilentInfo) {
            eventNamePrefix +=
                params.ssoSilentInfo.ssoSilentStatus ===
                    0 /* SsoSilentStatus.ssoSilentSuccess */
                    ? 'SsoSuccess_'
                    : 'SsoFail_';
        }
        var eventName = eventNamePrefix + param.params.tokenConsumer;
        logRequest({
            eventName: eventName,
            httpCode: param.success ? 200 : 500,
            latency: Math.round(param.latency),
            fromCache: param.fromCache,
            error: getErrorString(param.error),
        });
    }
}
function getSuccessResponse(type, tokenResponse) {
    var _a, _b;
    if (type == GetTokenType.NESTED_APP) {
        return {
            success: true,
            token: tokenResponse.accessToken,
            format: 'JWT',
            source: 'OfficeHome',
            expiryMs: (_a = tokenResponse.expiresOn) === null || _a === void 0 ? void 0 : _a.getTime(),
            idToken: tokenResponse.idToken,
            accountInfo: tokenResponse.account,
        };
    }
    else {
        return {
            success: true,
            token: tokenResponse.accessToken,
            format: 'JWT',
            source: 'OfficeHome',
            expiryMs: (_b = tokenResponse.expiresOn) === null || _b === void 0 ? void 0 : _b.getTime(),
        };
    }
}
function getFailureResponse(error) {
    return {
        success: false,
        error: error,
        token: '',
        format: 'JWT',
        source: 'OfficeHome',
    };
}
function getConditionalAccessErrorType(subError) {
    // case values for switch statement are taken from InteractionRequiredAuthSubErrorMessage defined here: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/2cde73f9cfffca9df6e5e084dd0ea255abca6d47/lib/msal-common/src/error/InteractionRequiredAuthError.ts
    // If msal.js exports InteractionRequiredAuthSubErrorMessage, use that instead of explicitly duplicating the values here.
    switch (subError) {
        case 'message_only':
            return 'ConditionalAccess_MessageOnly';
        case 'consent_required':
            return 'ConditionalAccess_ConsentRequired';
        default:
            return 'ConditionalAccess';
    }
}
//# sourceMappingURL=getToken.js.map